
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/SupabaseAuthContext';
import { hasPermission } from '@/utils/roleManagement';
import { UserRole } from '@/types/userRole';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredPermission?: string;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ 
  children, 
  requiredRole, 
  requiredPermission 
}) => {
  const { user, profile, loading } = useAuth();
  const isAuthenticated = !!user;
  const isLoading = loading;
  
  // While checking authentication, show a loading state
  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }
  
  // If not authenticated, redirect to auth page
  if (!isAuthenticated || !user) {
    return <Navigate to="/auth" replace />;
  }
  
  // If a specific role is required but user doesn't have it
  if (requiredRole && profile?.role !== requiredRole) {
    // User has incorrect role, redirect to appropriate dashboard
    
    // Redirect to appropriate dashboard based on user's actual role
    let redirectPath = '/dashboard';
    
    if (profile?.role === 'admin') {
      redirectPath = '/admin/dashboard';
    } else if (profile?.role === 'superadmin') {
      redirectPath = '/super-admin/dashboard';
    } else if (profile?.role === 'masterAdmin') {
      redirectPath = '/master/dashboard';
    } else if (profile?.role === 'business') {
      redirectPath = '/business/dashboard';
    } else if (profile?.role === 'user') {
      redirectPath = '/user/dashboard';
    } else if (profile?.role === 'manager') {
      redirectPath = '/admin/dashboard';
    }
    
    return <Navigate to={redirectPath} replace />;
  }
  
  // If a specific permission is required but user doesn't have it
  if (requiredPermission && !hasPermission(profile?.role as UserRole, requiredPermission)) {
    // User lacks required permission, redirect to appropriate dashboard
    
    // Redirect to the appropriate dashboard based on role
    let redirectPath = '/dashboard';
    
    if (profile?.role === 'admin' || profile?.role === 'manager') {
      redirectPath = '/admin/dashboard';
    } else if (profile?.role === 'superadmin') {
      redirectPath = '/super-admin/dashboard';
    } else if (profile?.role === 'masterAdmin') {
      redirectPath = '/master/dashboard';
    } else if (profile?.role === 'business') {
      redirectPath = '/business/dashboard';
    } else if (profile?.role === 'user') {
      redirectPath = '/user/dashboard';
    }
    
    return <Navigate to={redirectPath} replace />;
  }
  
  return <>{children}</>;
};

export default PrivateRoute;
